import { ActionButton, Button, Text } from '@react-spectrum/s2';
import Add from '@react-spectrum/s2/icons/Add';
import ChevronLeft from '@react-spectrum/s2/icons/ChevronLeft';
import { useRef, useState } from 'react';

import type { StrategySeed } from './DockMenu';
import {
	GroundingFlow,
	type GroundingFlowHandle,
	type GroundingProgress,
} from './GroundingFlow';

// Creating a strategy is a VIEW of the dock menu, peer to the list and the detail — not a
// dialog stacked on top of it. Its header is the detail's eyebrow row with the label naming
// this view ([‹ Back] "New Strategy"), so moving between them never changes the chrome.
export function StrategyCreateView({
	businessId,
	businessLabel,
	seed,
	onBack,
	onGenerate,
}: {
	businessId: string;
	// Titles the brief ("# Grounding: <label>"). Optional so existing callers keep working.
	businessLabel?: string;
	// Pre-filled answers from a propose_new_strategy recommendation. When present the grounding
	// flow starts with the agent's suggestions already populated so the owner can review and edit.
	seed?: StrategySeed;
	// Returns to the card list — also where a successful generate lands, so the owner watches
	// the new strategy appear instead of being dropped out of the menu.
	onBack: () => void;
	// Resolves on success; rejects with owner-facing copy.
	onGenerate: () => Promise<void>;
}) {
	const docRef = useRef<GroundingFlowHandle>(null);
	const [progress, setProgress] = useState<GroundingProgress | null>(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const generate = async () => {
		if (busy) return;
		setError(null);
		setBusy(true);
		// Flush the brief first so a typed-but-unblurred edit reaches generation; best-effort
		// (a failed flush still proceeds against the last save, never blocks the run).
		await docRef.current?.flush().catch(() => null);
		try {
			await onGenerate();
			onBack();
		} catch (err) {
			setBusy(false);
			setError(
				err instanceof Error
					? err.message
					: "We couldn't start your strategy. Please try again.",
			);
		}
	};

	return (
		<div
			data-testid="strategy-create-view"
			className="flex min-h-0 w-full flex-1 flex-col px-5"
		>
			<div className="flex flex-shrink-0 items-center gap-1 pb-4">
				<ActionButton
					isQuiet
					aria-label="Back"
					isDisabled={busy}
					onPress={onBack}
				>
					<ChevronLeft />
				</ActionButton>
				<span className="text-sm text-(--text-muted)">New Strategy</span>
			</div>
			<div className="flex min-h-0 flex-1 flex-col gap-3 pb-5">
				<GroundingFlow
					ref={docRef}
					onProgress={setProgress}
					businessId={businessId}
					label={businessLabel ?? 'your business'}
					strategySeed={seed}
					// Generate stays owned by this view — it is the view's action, and moving it
					// into the flow would leave the pre-flight ordering tests asserting a mock.
					footerAction={
						<Button
							variant="accent"
							isPending={busy}
							isDisabled={busy}
							onPress={() => void generate()}
						>
							<Add />
							<Text>Generate</Text>
						</Button>
					}
				/>
				{/* The panel's own controls, below the conversation rather than inside it. */}
				{progress?.step != null && (
					<div
						className="flex flex-shrink-0 items-center gap-3"
						data-testid="grounding-actions"
					>
						<Button
							variant="secondary"
							isDisabled={!progress.canGoBack}
							onPress={progress.onBack}
						>
							<Text>Back</Text>
						</Button>
						<span className="text-xs text-(--text-muted) tabular-nums">
							{progress.remaining > 0
								? `${progress.remaining} remaining`
								: "That's everything"}
						</span>
						{!progress.writingOwn && (
							<span className="ml-auto text-xs text-(--text-muted)">
								Pick an answer, or write your own
							</span>
						)}
					</div>
				)}
				{error && (
					<p
						className="flex-shrink-0 text-sm text-red-600"
						data-testid="generate-error"
					>
						{error}
					</p>
				)}
			</div>
		</div>
	);
}
